import { GrowthDashboardMidCardInfo } from '@/api/growth/dashboard/type';

interface GrowthDashboardCardProps {
    cardInfo: GrowthDashboardMidCardInfo | null;
    displayType: number;
}

interface CardConfig {
    mainTitle: string;
    mainValue: string;
    subTitle: string;
    subValue: string;
    icon: string;
    color: string;
}

const GrowthDashboardCard = ({
    cardInfo,
    displayType,
}: GrowthDashboardCardProps) => {
    if (!cardInfo) {
        return null;
    }

    // 파종방법 매핑
    const getPlmeLabel = (code: string): string => {
        const plmeMap: Record<string, string> = {
            B: '고랑파종',
            S: '건조종자직파',
            C: '삽목',
            H: '수평삽목',
            I: '경사삽목',
            N: '육묘이식',
            P: '발아종자파종',
            R: '재생재배',
            T: '이식재배',
            V: '수직삽목',
        };
        return plmeMap[code] || code;
    };

    // 심는방법 매핑
    // const getPldsLabel = (code: string): string => {
    //     const pldsMap: Record<string, string> = {
    //         R: '조파',
    //         H: '점파',
    //         U: '산파',
    //     };
    //     return pldsMap[code] || code;
    // };

    const getCardConfig = (): CardConfig => {
        switch (displayType) {
            case 1: // 파종일자
                return {
                    mainTitle: '파종일자',
                    mainValue: cardInfo.pdate || '-',
                    subTitle: '평균보다',
                    subValue: cardInfo.diffDays
                        ? `${cardInfo.diffDays > 0 ? '+' : ''}${cardInfo.diffDays}일`
                        : '-',
                    icon: 'ri-seedling-fill',
                    color: 'success',
                };
            case 2: // 파종방법
                return {
                    mainTitle: '파종방법',
                    mainValue: cardInfo.plme
                        ? getPlmeLabel(cardInfo.plme)
                        : '-',
                    subTitle: '인기방법',
                    subValue: cardInfo.mostCommonPlme
                        ? getPlmeLabel(cardInfo.mostCommonPlme)
                        : '-',
                    icon: 'ri-flower-line',
                    color: 'primary',
                };
            case 3: // 파종간격/깊이
                return {
                    mainTitle: '파종간격/깊이',
                    mainValue:
                        cardInfo.plrs && cardInfo.pldp
                            ? `${cardInfo.plrs}cm/${cardInfo.pldp}cm`
                            : '-',
                    subTitle: '제일많은방법',
                    subValue:
                        cardInfo.mostCommonPlrs && cardInfo.mostCommonPldp
                            ? `${cardInfo.mostCommonPlrs}cm/${cardInfo.mostCommonPldp}cm`
                            : '-',
                    icon: 'ri-ruler-line',
                    color: 'secondary',
                };
            case 4: // 파종량
                return {
                    mainTitle: '1m²당 종자수',
                    mainValue: cardInfo.ppop ? `${cardInfo.ppop}개` : '-',
                    subTitle: '평균보다',
                    subValue:
                        cardInfo.ppop && cardInfo.avgPpop
                            ? `${parseFloat(cardInfo.ppop) - parseFloat(cardInfo.avgPpop) > 0 ? '+' : ''}${(parseFloat(cardInfo.ppop) - parseFloat(cardInfo.avgPpop)).toFixed(0)}개`
                            : '-',
                    icon: 'ri-scales-fill',
                    color: 'pink',
                };
            case 5: // 수분흡수량
                return {
                    mainTitle: '수분흡수량',
                    mainValue: cardInfo.waterAsorption
                        ? `${cardInfo.waterAsorption.toFixed(1)}mm`
                        : '-',
                    subTitle: '수분사용량',
                    subValue: cardInfo.waterUsage
                        ? `${cardInfo.waterUsage.toFixed(1)}mm`
                        : '-',
                    icon: 'ri-contrast-drop-2-line',
                    color: 'info',
                };
            case 6: // 수확까지 남은일수
                return {
                    mainTitle: '수확까지 남은일수',
                    mainValue:
                        cardInfo.daysUntilHarvest == 0 ||
                        cardInfo.daysUntilHarvest > 0
                            ? `${cardInfo.daysUntilHarvest}일`
                            : '-',
                    subTitle: '생육진행일수',
                    subValue: cardInfo.dap ? `${cardInfo.dap}일` : '-',
                    icon: 'ri-calendar-schedule-line',
                    color: 'danger',
                };
            default:
                return {
                    mainTitle: '-',
                    mainValue: '-',
                    subTitle: '-',
                    subValue: '-',
                    icon: 'ri-question-line',
                    color: 'secondary',
                };
        }
    };

    const config = getCardConfig();

    return (
        <div className={`custom-card card project-cards ${config.color}`}>
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-1">
                    <div>
                        <div className="fs-13 align-middle text-muted mb-1">
                            {config.mainTitle}
                        </div>
                        <div className="text-muted fs-12 mb-1">
                            <span
                                className={`text-${config.color} fw-semibold fs-18`}
                            >
                                {config.mainValue}
                            </span>
                        </div>
                        <div className="d-flex gap-2 justify-content-between flex-wrap align-items-center">
                            <div className="fs-12 mb-0">{config.subTitle}</div>
                            <div className="badge bg-danger-transparent rounded-pill">
                                {config.subValue}
                            </div>
                        </div>
                    </div>
                    <div
                        className={`text-${config.color} p-1 rounded-circle border border-${config.color} border-opacity-25 shadow-sm ms-auto`}
                    >
                        <div
                            className={`p-2 bg-${config.color} bg-opacity-25 rounded-circle`}
                        >
                            <i className={`${config.icon} fs-18 xy20`}></i>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GrowthDashboardCard;
